// src/components/review/ErelEntityCard.js
// Card de uma proposta entity_create no Review Workbench EREL (G3B_SPEC v1.0).
//
// Comportamento:
//   - colapsado: company_name em destaque + domínio de origem + recommended_action
//   - expandir inicia o cronômetro decided_in_seconds (para no clique de confirmação)
//     e dispara o preview dry-run fn_erel_apply_proposal(id, NULL, NULL, true)
//   - preview pode falhar com exceção contratual (proposta obsoleta / similaridade §4)
//     → a mensagem é exibida literalmente no bloco de preview (é orientação, não bug)
//   - 6 botões (§3); clique abre picker de reason (tipos_review_reason §2.3)
//   - human_note obrigatória quando requires_note=true OU botão merge_candidate,
//     validada no client antes do RPC

import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Check, X, BookMarked, Search, GitMerge, Ban, ChevronDown, ChevronUp,
  Globe, Loader2, AlertCircle, Building2, Link2, PlusCircle,
} from 'lucide-react';
import { supabase } from '../../supabaseClient';

const DECISION_BUTTONS = [
  {
    key: 'approve', label: 'Aprovar', icon: Check,
    cls: 'text-white bg-emerald-600 border-emerald-600 hover:bg-emerald-700',
  },
  {
    key: 'reject', label: 'Rejeitar', icon: X,
    cls: 'text-red-700 bg-white border-red-200 hover:bg-red-50',
  },
  {
    key: 'mark_reference_only', label: 'Só referência', icon: BookMarked,
    cls: 'text-blue-700 bg-white border-blue-200 hover:bg-blue-50',
  },
  {
    key: 'needs_more_research', label: 'Pesquisar mais', icon: Search,
    cls: 'text-amber-700 bg-white border-amber-200 hover:bg-amber-50',
  },
  {
    key: 'merge_candidate', label: 'Merge candidate', icon: GitMerge,
    cls: 'text-purple-700 bg-white border-purple-200 hover:bg-purple-50',
  },
  {
    key: 'block', label: 'Bloquear', icon: Ban,
    cls: 'text-slate-700 bg-white border-slate-300 hover:bg-slate-100',
  },
];

const BUTTON_LABEL = Object.fromEntries(DECISION_BUTTONS.map((b) => [b.key, b.label]));

const RECOMMENDED_TONE = {
  approve: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  reject: 'bg-red-100 text-red-800 border-red-200',
  review: 'bg-amber-100 text-amber-800 border-amber-200',
};

function getField(proposedData, name) {
  const fields = Array.isArray(proposedData?.fields) ? proposedData.fields : [];
  const f = fields.find((x) => x?.field === name);
  return f?.value ?? null;
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function fmtConfidence(c) {
  const n = Number(c);
  if (c == null || isNaN(n)) return '—';
  return `${Math.round(n * 100)}%`;
}

export default function ErelEntityCard({ proposal, busy, fetchReasons, onDecide }) {
  const [expanded, setExpanded] = useState(false);
  const [preview, setPreview] = useState({ status: 'idle', plan: null, error: null });
  // flow = picker de reason aberto: {button, reasons, loading, error, reasonValue, note}
  const [flow, setFlow] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const startedAtRef = useRef(null); // cronômetro: inicia no 1º expand, para na confirmação

  const pd = useMemo(() => proposal.proposed_data || {}, [proposal.proposed_data]);
  const companyName = useMemo(() => getField(pd, 'company_name'), [pd]);
  const fieldDomain = useMemo(() => getField(pd, 'domain'), [pd]);
  // G3B-PATCH-01: o builder grava recommended_action na raiz de proposed_data;
  // governance.recommended_action é fallback de compatibilidade
  const recommendedAction = pd.recommended_action || pd.governance?.recommended_action || null;

  const loadPreview = useCallback(async () => {
    setPreview({ status: 'loading', plan: null, error: null });
    const { data, error } = await supabase.rpc('fn_erel_apply_proposal', {
      p_proposal_id: proposal.id,
      p_reviewer: null,
      p_reason_code: null,
      p_dry_run: true,
    });
    if (error) setPreview({ status: 'error', plan: null, error: error.message });
    else setPreview({ status: 'ready', plan: data, error: null });
  }, [proposal.id]);

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) {
      if (startedAtRef.current == null) startedAtRef.current = Date.now();
      if (preview.status === 'idle') loadPreview();
    }
  };

  const openFlow = async (button) => {
    setFlow({ button, reasons: [], loading: true, error: null, reasonValue: '', note: '' });
    try {
      const reasons = await fetchReasons(button);
      setFlow((f) => (f && f.button === button ? { ...f, reasons, loading: false } : f));
    } catch (e) {
      setFlow((f) => (f && f.button === button
        ? { ...f, loading: false, error: e?.message || 'Erro ao carregar motivos' }
        : f));
    }
  };

  const selectedReason = flow?.reasons?.find((r) => r.value === flow.reasonValue) || null;
  const noteRequired = !!(selectedReason?.requires_note) || flow?.button === 'merge_candidate';
  const noteOk = !noteRequired || (flow?.note || '').trim() !== '';
  const canConfirm = !!flow && !flow.loading && !flow.error && !!flow.reasonValue && noteOk;

  const handleConfirm = async () => {
    if (!canConfirm || submitting) return;
    const seconds = startedAtRef.current != null
      ? Math.round((Date.now() - startedAtRef.current) / 1000)
      : null;
    setSubmitting(true);
    const ok = await onDecide({
      proposalId: proposal.id,
      decision: flow.button,
      reasonCode: flow.reasonValue,
      humanNote: (flow.note || '').trim() || null,
      decidedInSeconds: seconds,
    });
    setSubmitting(false);
    if (ok) setFlow(null); // sucesso: lista recarrega e o card sai
  };

  const plan = preview.plan || {};
  const willWrite = plan.will_write_empresas || null;
  const notApplied = Array.isArray(plan.not_applied_fields) ? plan.not_applied_fields : [];
  const disabled = busy || submitting;

  return (
    <div
      className={`bg-white border border-slate-200 rounded-lg transition-opacity ${
        disabled ? 'opacity-60 pointer-events-none' : ''
      }`}
    >
      {/* Header — sempre visível; clique expande e inicia o cronômetro */}
      <button
        type="button"
        onClick={handleToggle}
        className="w-full text-left p-4 flex items-start gap-3"
        aria-expanded={expanded}
      >
        <Building2 size={18} className="text-indigo-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-base font-bold text-slate-900 leading-snug">
            {companyName || '(sem company_name)'}
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            {proposal.raw_mention && (
              <span className="flex items-center gap-1 text-[11px] text-slate-600 font-mono">
                <Globe size={11} className="text-slate-400" />
                {proposal.raw_mention}
              </span>
            )}
            {recommendedAction && (
              <span
                className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border ${
                  RECOMMENDED_TONE[recommendedAction] || 'bg-slate-100 text-slate-700 border-slate-200'
                }`}
                title="Ação recomendada pelo proposal builder (G3A)"
              >
                IA sugere: {recommendedAction}
              </span>
            )}
            <span className="text-[11px] text-slate-400">·</span>
            <span className="text-[11px] text-slate-500" title="Confiança média do agent">
              {fmtConfidence(proposal.ai_confidence)}
            </span>
            <span className="text-[11px] text-slate-400">·</span>
            <span className="text-[11px] text-slate-500">{fmtDate(proposal.created_at)}</span>
          </div>
        </div>
        {expanded
          ? <ChevronUp size={16} className="text-slate-400 flex-shrink-0 mt-1" />
          : <ChevronDown size={16} className="text-slate-400 flex-shrink-0 mt-1" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Preview dry-run (§2.2) */}
          <div className="bg-slate-50 border border-slate-200 rounded p-3">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-2">
              Preview do apply (dry-run)
            </div>

            {preview.status === 'loading' && (
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Loader2 size={13} className="animate-spin" /> Calculando preview…
              </div>
            )}

            {preview.status === 'error' && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2">
                <AlertCircle size={13} className="flex-shrink-0 mt-0.5" />
                <span>{preview.error}</span>
              </div>
            )}

            {preview.status === 'ready' && (
              <div className="space-y-2 text-xs text-slate-700">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase border ${
                      plan.mode === 'link'
                        ? 'bg-blue-100 text-blue-800 border-blue-200'
                        : 'bg-emerald-100 text-emerald-800 border-emerald-200'
                    }`}
                  >
                    {plan.mode === 'link' ? <Link2 size={11} /> : <PlusCircle size={11} />}
                    {plan.mode || '—'}
                  </span>
                  {plan.link_reason && (
                    <span className="text-[11px] text-slate-500 font-mono">{plan.link_reason}</span>
                  )}
                </div>

                {plan.mode === 'link' && plan.target_empresa_nome && (
                  <div>
                    <span className="text-slate-500">Vincula à empresa existente: </span>
                    <span className="font-semibold">{plan.target_empresa_nome}</span>
                  </div>
                )}

                {willWrite && (
                  <div>
                    <div className="text-slate-500 mb-1">Escreverá em empresas:</div>
                    <ul className="space-y-0.5">
                      {Object.entries(willWrite).map(([k, v]) => (
                        <li key={k} className="font-mono text-[11px]">
                          <span className="text-slate-400">{k}:</span> {String(v)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div>
                  <span className="text-slate-500">Vínculo de domínio: </span>
                  {plan.will_link_domain ? (
                    <span className="font-mono text-[11px] font-semibold">{plan.will_link_domain}</span>
                  ) : (
                    <span className="text-slate-400">nenhum (sem row de domínio)</span>
                  )}
                </div>

                {notApplied.length > 0 && (
                  <div>
                    <div className="text-slate-500 mb-1" title="Permanecem em proposed_data para o futuro fluxo de enrich (§5.d)">
                      Campos que NÃO serão escritos:
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {notApplied.map((f) => (
                        <span
                          key={f}
                          className="px-1.5 py-0.5 bg-slate-200 text-slate-600 rounded text-[10px] font-mono"
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {fieldDomain && proposal.raw_mention
            && fieldDomain.toLowerCase() !== String(proposal.raw_mention).toLowerCase() && (
            <div className="text-[11px] text-slate-500">
              Domínio proposto pela IA: <span className="font-mono">{fieldDomain}</span>
            </div>
          )}

          {/* 6 botões de decisão (§3) */}
          {!flow && (
            <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-slate-100">
              {DECISION_BUTTONS.map(({ key, label, icon: Icon, cls }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => openFlow(key)}
                  disabled={disabled}
                  className={`px-3 py-1.5 text-xs font-medium border rounded disabled:opacity-50 flex items-center gap-1.5 ${cls}`}
                >
                  <Icon size={13} /> {label}
                </button>
              ))}
            </div>
          )}

          {/* Picker de reason + human_note (§2.3) */}
          {flow && (
            <div className="border border-slate-300 rounded p-3 space-y-2 bg-white">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-slate-800">
                  {BUTTON_LABEL[flow.button] || flow.button} — motivo
                </div>
                <button
                  type="button"
                  onClick={() => setFlow(null)}
                  disabled={submitting}
                  className="text-[11px] text-slate-500 hover:text-slate-800"
                >
                  Cancelar
                </button>
              </div>

              {flow.loading && (
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Loader2 size={13} className="animate-spin" /> Carregando motivos…
                </div>
              )}

              {flow.error && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
                  {flow.error}
                </div>
              )}

              {!flow.loading && !flow.error && flow.reasons.length === 0 && (
                <div className="text-xs text-slate-500">
                  Nenhum motivo ativo para este botão.
                </div>
              )}

              {!flow.loading && !flow.error && flow.reasons.length > 0 && (
                <div className="space-y-1">
                  {flow.reasons.map((r) => (
                    <label
                      key={r.value}
                      className={`flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer text-xs border ${
                        flow.reasonValue === r.value
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-transparent hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`reason-${proposal.id}`}
                        checked={flow.reasonValue === r.value}
                        onChange={() => setFlow((f) => (f ? { ...f, reasonValue: r.value } : f))}
                        className="mt-0.5 accent-blue-600"
                      />
                      <span className="flex-1">
                        <span className="text-slate-800">{r.label_pt}</span>{' '}
                        <span className="text-slate-400 font-mono text-[10px]">{r.value}</span>
                        {r.requires_note && (
                          <span className="ml-1 text-[10px] text-amber-700">(exige nota)</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}

              <div>
                <label className="block text-[11px] text-slate-600 mb-1">
                  Nota{noteRequired
                    ? <span className="text-red-600 font-semibold"> (obrigatória{flow.button === 'merge_candidate' ? ' — indique a empresa-alvo do merge' : ''})</span>
                    : ' (opcional)'}
                </label>
                <textarea
                  value={flow.note}
                  onChange={(e) => setFlow((f) => (f ? { ...f, note: e.target.value } : f))}
                  rows={2}
                  className="w-full text-xs border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  placeholder={flow.button === 'merge_candidate'
                    ? 'Empresa-alvo do merge e contexto…'
                    : 'Contexto da decisão…'}
                />
              </div>

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={!canConfirm || submitting}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
                >
                  {submitting && <Loader2 size={13} className="animate-spin" />}
                  Confirmar {BUTTON_LABEL[flow.button] || flow.button}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
